import { Controller } from "@hotwired/stimulus"

// The one bottom strip above the tab bar, shared by every app-wide banner:
// feature announcements, the push opt-in, the PWA install prompt. Each decides
// for itself whether it has anything to say — the announcement server-side, the
// other two from browser state that only settles after connect — so without a
// referee they all claim the same fixed coordinates and stack on top of each
// other on a phone.
//
// This keeps the first banner in DOM order that wants the space and suppresses
// the rest. DOM order is priority order.
export default class extends Controller {
  static targets = ["banner"]

  connect() {
    // The push and install prompts reveal themselves asynchronously (permission
    // checks, a late `beforeinstallprompt`), so re-run whenever anything inside
    // the slot changes rather than deciding once at connect.
    this.observer = new MutationObserver(() => this.update())
    this.update()
  }

  disconnect() {
    this.observer.disconnect()
  }

  // Measures with our own suppression lifted, so a banner that lost the slot
  // earlier can reclaim it once the one above it goes quiet. Observation is
  // paused across the pass — every write here is ours, and reacting to them
  // would loop.
  update() {
    this.observer.disconnect()
    this.bannerTargets.forEach((banner) => { banner.style.display = "" })

    let claimed = false
    for (const banner of this.bannerTargets) {
      // A banner whose content is hidden collapses to zero height; that is the
      // only honest signal here, since each owner hides an inner element rather
      // than the root this slot can see.
      if (banner.getBoundingClientRect().height === 0) continue
      if (claimed) banner.style.display = "none"
      claimed = true
    }

    this.observer.observe(this.element, {
      attributes: true,
      attributeFilter: [ "hidden", "style", "class" ],
      childList: true,
      subtree: true
    })
  }
}
